import { DrawerMode } from '@billy/design-system'
import { TFunction } from 'i18next'

import { AttachmentFilterType } from '@features/attachments/enums/attachmentFiltertype'
import { requestBankDemoAutoOpen } from '@features/bankAccounts/utils/bankDemoAutoOpenStore'
import { requestConnectBankAutoOpen } from '@features/bankAccounts/utils/connectBankModalUiSourceStore'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { getInvoiceDocumentRouteBase } from '@features/invoices/utils/getInvoiceDocumentRouteBase'
import { QuickStartStep } from '@features/quickStart/enums'
import { UiSource } from '@features/reconciliation/enums'

import { getOrganizationViewUrl } from '../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../utils/routing/reactRoute'
import { QuickStartAction } from '../types/quickStartAction'

interface QuickStartItem {
  description?: string
  id: QuickStartStep
  primaryAction?: QuickStartAction
  secondaryAction?: QuickStartAction
  title: string
}

interface GetQuickStartItemsParams {
  isBankDemoEnabled: boolean
  isCpa: boolean
  isDemoInvoiceEditorEnabled: boolean
  organizationUrl?: string
  settingsOrganizationUrl?: string
  t: TFunction
}

const directStepOrder = [
  QuickStartStep.CreateAccount,
  QuickStartStep.Account,
  QuickStartStep.Invoice,
  QuickStartStep.Purchases,
  QuickStartStep.EInvoicing,
  QuickStartStep.Bank,
]

const cpaStepOrder = [
  QuickStartStep.CreateAccount,
  QuickStartStep.Account,
  QuickStartStep.Purchases,
  QuickStartStep.EInvoicing,
  QuickStartStep.Invoice,
  QuickStartStep.Bank,
]

const getInvoiceStepDescriptionKey = (isCpa: boolean, isDemoInvoiceEditorEnabled: boolean) => {
  if (isDemoInvoiceEditorEnabled) {
    return 'quick_start.steps.invoice.description_demo'
  }

  return isCpa ? 'quick_start.steps.invoice.description_cpa' : 'quick_start.steps.invoice.description'
}

const getInvoiceQuickStartItem = ({
  isCpa,
  isDemoInvoiceEditorEnabled,
  organizationUrl,
  t,
}: GetQuickStartItemsParams): QuickStartItem => {
  const description = t(getInvoiceStepDescriptionKey(isCpa, isDemoInvoiceEditorEnabled))
  const primaryAction: QuickStartAction = {
    label: t('quick_start.steps.invoice.primary_action'),
    to: getOrganizationViewUrl(
      organizationUrl,
      reactRoute.organization.invoices.getNewDocumentRoute(getInvoiceDocumentRouteBase(InvoiceDocumentType.Invoice)),
    ),
    trackingEvent: 'XXX - AG - Quick Start - Create an invoice clicked',
  }

  if (isDemoInvoiceEditorEnabled) {
    return {
      description,
      id: QuickStartStep.Invoice,
      primaryAction,
      secondaryAction: {
        icon: 'sparkles',
        label: t('quick_start.steps.invoice.secondary_action_demo'),
        to: getOrganizationViewUrl(organizationUrl, reactRoute.organization.demoInvoice.getMainRoute()),
        trackingEvent: 'XXX - AG - Quick Start - Start Invoice Demo clicked',
      },
      title: t('quick_start.steps.invoice.title_demo'),
    }
  }

  return {
    description,
    id: QuickStartStep.Invoice,
    primaryAction,
    secondaryAction: {
      label: t('quick_start.steps.invoice.secondary_action'),
      to: getOrganizationViewUrl(
        organizationUrl,
        reactRoute.organization.invoices.getNewDocumentRoute(getInvoiceDocumentRouteBase(InvoiceDocumentType.Quote)),
      ),
      trackingEvent: 'XXX - AG - Quick Start - Start with a quote clicked',
    },
    title: t(isCpa ? 'quick_start.steps.invoice.title_cpa' : 'quick_start.steps.invoice.title'),
  }
}

export const getQuickStartItems = (params: GetQuickStartItemsParams): QuickStartItem[] => {
  const { isBankDemoEnabled, isCpa, organizationUrl, settingsOrganizationUrl, t } = params

  const itemsByStep: Record<QuickStartStep, QuickStartItem> = {
    [QuickStartStep.CreateAccount]: {
      id: QuickStartStep.CreateAccount,
      title: t(isCpa ? 'quick_start.steps.created_account.title_cpa' : 'quick_start.steps.created_account.title'),
    },
    [QuickStartStep.Account]: {
      description: t(isCpa ? 'quick_start.steps.account.description_cpa' : 'quick_start.steps.account.description'),
      id: QuickStartStep.Account,
      primaryAction: {
        label: t(isCpa ? 'quick_start.steps.account.primary_action_cpa' : 'quick_start.steps.account.primary_action'),
        to: settingsOrganizationUrl,
        trackingEvent: 'XXX - AG - Quick Start - Fill in your details clicked',
      },
      title: t(isCpa ? 'quick_start.steps.account.title_cpa' : 'quick_start.steps.account.title'),
    },
    [QuickStartStep.Invoice]: getInvoiceQuickStartItem(params),
    [QuickStartStep.Purchases]: {
      description: t('quick_start.steps.purchases.description'),
      id: QuickStartStep.Purchases,
      primaryAction: {
        label: t(
          isCpa ? 'quick_start.steps.purchases.primary_action_cpa' : 'quick_start.steps.purchases.primary_action',
        ),
        to: getOrganizationViewUrl(organizationUrl, reactRoute.organization.uploads.getMainRoute(), {
          drawerMode: DrawerMode.Opened,
          type: AttachmentFilterType.Scout,
        }),
        trackingEvent: 'XXX - AG - Quick Start - Automate receipt collection clicked',
      },
      secondaryAction: {
        label: t('quick_start.steps.purchases.secondary_action'),
        to: getOrganizationViewUrl(organizationUrl, reactRoute.organization.uploads.getMainRoute()),
        trackingEvent: 'XXX - AG - Quick Start - Log your expense clicked',
      },
      title: t(isCpa ? 'quick_start.steps.purchases.title_cpa' : 'quick_start.steps.purchases.title'),
    },
    [QuickStartStep.EInvoicing]: {
      description: t(
        isCpa ? 'quick_start.steps.e_invoicing.description_cpa' : 'quick_start.steps.e_invoicing.description',
      ),
      id: QuickStartStep.EInvoicing,
      primaryAction: {
        label: t('quick_start.steps.e_invoicing.primary_action'),
        to: getOrganizationViewUrl(organizationUrl, reactRoute.organization.eInvoicing.getMainRoute()),
        trackingEvent: 'XXX - AG - Quick Start - Connect now clicked',
      },
      title: t(isCpa ? 'quick_start.steps.e_invoicing.title_cpa' : 'quick_start.steps.e_invoicing.title'),
    },
    [QuickStartStep.Bank]: {
      description: t('quick_start.steps.bank.description'),
      id: QuickStartStep.Bank,
      primaryAction: {
        label: t('quick_start.steps.bank.primary_action'),
        onClick: () => requestConnectBankAutoOpen(UiSource.QuickStart),
        to: getOrganizationViewUrl(organizationUrl, reactRoute.organization.bank.getBankAccountsRoute()),
        trackingEvent: 'XXX - AG - Quick Start - Link your account clicked',
      },
      secondaryAction: isBankDemoEnabled
        ? {
            icon: 'sparkles',
            label: t('quick_start.steps.bank.secondary_action'),
            onClick: requestBankDemoAutoOpen,
            to: getOrganizationViewUrl(organizationUrl, reactRoute.organization.bank.getBankAccountsRoute()),
            trackingEvent: 'XXX - AG - Quick Start - Start Bank Demo clicked',
          }
        : undefined,
      title: t('quick_start.steps.bank.title'),
    },
  }

  return (isCpa ? cpaStepOrder : directStepOrder).map((step) => itemsByStep[step])
}
